import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { KqmLogo } from "@/components/KqmLogo";
import { AmdenLogo } from "@/components/AmdenLogo";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { slug } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    if (slug) {
      api.get(`/companies/by-slug/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
    }
  }, [slug]);

  const brandName = brand?.name || "Amden Pulse";
  const accent = brand?.primary_color || "#0F172A";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "employee" ? "/app" : u.role === "amden_super" ? "/amden" : u.role === "kqm_manager" ? "/manager" : "/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };

  const forgot = async () => {
    if (!email.trim()) { toast.error("Enter your email above first, then tap again"); return; }
    try { await api.post("/auth/forgot-password", { email: email.trim() }); }
    catch { /* always show the same message */ }
    toast.success("If that email exists, a reset link is on its way.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-white" style={{ backgroundColor: accent }}>
        <div className="flex items-center gap-3">
          {brand ? <BrandLogo company={brand} dark className="h-9" /> : <AmdenLogo dark className="h-10" />}
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 leading-tight">Workforce &<br />Job Costing</div>
        </div>
        <div className="relative z-10">
          <Zap className="h-10 w-10 text-amber-500 mb-6" />
          <h1 className="font-heading text-4xl font-black leading-tight tracking-tight">
            Every hour.<br />Every site.<br /><span className="text-amber-500">Every euro.</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-sm">
            A complete workforce and profitability management system for multi-site businesses. Manage employee records, timesheets, training and compliance while tracking fully loaded employment costs and real-time profitability across every site.
          </p>
        </div>
        <div className="text-xs text-white/40">Amden Systems • Europe/Dublin • EUR</div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-amber-500/10" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-[#FAFAFA]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8">
            {brand ? <BrandLogo company={brand} className="h-9" /> : <AmdenLogo className="h-9" />}
          </div>
          {brand && <div data-testid="brand-name" className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">{brandName}</div>}
          <h2 className="font-heading text-2xl font-bold text-zinc-950">Sign in</h2>
          <p className="text-sm text-zinc-500 mb-6">Access your workforce dashboard.</p>

          {error && <div data-testid="login-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 mt-1" placeholder="you@company.ie" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
            <button type="button" data-testid="login-forgot-password-link" onClick={forgot} className="w-full text-center text-xs text-zinc-500 hover:text-slate-900 underline">Forgot your password?</button>
          </div>

          {!slug && (
            <div className="mt-8 border-t border-zinc-200 pt-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Demo logins</div>
              <div className="grid gap-2 text-xs">
                <button type="button" data-testid="quick-admin" onClick={() => quick("nora@brightspark.ie", "Spark#2026")} className="text-left px-3 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors">
                  <b>Client Admin</b> — Nora Brady (Bright Spark)
                </button>
                <button type="button" data-testid="quick-employee" onClick={() => quick("oisin.nolan@brightspark.ie", "Worker#2026")} className="text-left px-3 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors">
                  <b>Employee</b> — Oisin Nolan (Bright Spark)
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
